/**
 * Documents Data Source
 */

import type { 
  DataSourceModule, 
  FieldMetadata, 
  ColumnDefinition, 
  ListWidgetItem,
  TableDataOutput,
  TableItemsOutput,
  TableDataRow,
  DataSourceConfig,
  TransformerContext
} from '../types';
import { getCachedDocumentItems, getCachedDocumentItemsByFolder, DocumentItem } from "@/lib/stores/documentStore";
import { getPendingChangeById } from "@/lib/stores/pendingChangesStore";
import { formatDate, formatTags, formatSectionCount } from '../shared/formatters';
import { CardData } from '@/components/layout/element-layout/grid-layout/GridLayout';
import { formatRelativeTime, convertTagIdsToNames } from "@/lib/utils";
import { getStatusColor } from "@/lib/utils/statusColors";

// Field definitions with metadata
const fields: Record<string, FieldMetadata> = {
  id: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
  },
  name: {
    type: 'string',
    editable: 'text',
    filterable: true,
    sortable: true,
    required: true,
  },
  description: {
    type: 'string',
    editable: 'text',
    filterable: true,
    sortable: false,
  },
  owner: {
    type: 'string',
    editable: 'dropdown',
    filterable: true,
    sortable: true,
    linkedDataSource: 'users',
    displayField: 'username',
    valueField: 'id',
  },
  tags: {
    type: 'array',
    editable: 'dropdown',
    filterable: true,
    sortable: false,
    linkedDataSource: 'tags',
    multiSelect: true,
    displayField: 'name',
    valueField: 'id',
    formatter: formatTags
  },
  status: {
    type: 'string',
    editable: 'dropdown',
    filterable: true,
    sortable: true,
    linkedDataSource: 'statuses',
  },
  folder: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
  },
  sections: {
    type: 'number',
    editable: false,
    filterable: false,
    sortable: true,
  },
  created_at: {
    type: 'date',
    editable: false,
    filterable: false,
    sortable: true,
    formatter: formatDate
  },
  updated_at: {
    type: 'date',
    editable: false,
    filterable: false,
    sortable: true,
    formatter: formatDate
  },
  created_by: {
    type: 'string',
    editable: false,
    filterable: true,
    sortable: true,
    linkedDataSource: 'users',
    displayField: 'username',
    valueField: 'id',
  }
};

// Column definitions
const columns: ColumnDefinition[] = [
  { key: 'name', label: 'Title', filterable: true, metadata: fields.name },
  { key: 'description', label: 'Description', filterable: true, metadata: fields.description },
  { key: 'owner', label: 'Owner', filterable: true, metadata: fields.owner },
  { key: 'tags', label: 'Tags', filterable: true, metadata: fields.tags },
  { key: 'status', label: 'Status', filterable: true, metadata: fields.status },
  { key: 'updated_at', label: 'Last Update', align: 'left', metadata: fields.updated_at },
];

/**
 * Column keys for documents table view (matching the UI expectations)
 */
const TABLE_COLUMNS = ["Title", "Description", "Owner", "Tags", "Status", "Last Update"];

// Configuration
const config: DataSourceConfig = {
  type: 'documents',
  title: 'Documents',
  description: 'Document items from the current game',
  expandable: false,
};

// Data fetcher - merges pending changes with cached data
function fetchData(selectedGame?: any, selectedFolderId?: string | null): DocumentItem[] {
  if (!selectedGame?.id) return [];
  
  // Get base data from cache
  let baseData: DocumentItem[];
  if (selectedFolderId !== undefined && selectedFolderId !== null) {
    baseData = getCachedDocumentItemsByFolder(selectedGame.id, selectedFolderId);
  } else {
    baseData = getCachedDocumentItems(selectedGame.id);
  }
  
  // Merge pending changes into the data
  // This ensures that local edits are immediately visible everywhere
  return baseData.map(item => {
    const pendingChange = getPendingChangeById(item.id);
    if (pendingChange && pendingChange.pendingData) {
      // Use pending data instead of cached data for items with local changes
      return {
        ...item,
        ...pendingChange.pendingData,
        // Ensure we keep the ID and game_id from the original
        id: item.id,
        game_id: item.game_id,
      };
    }
    return item;
  });
}

// Transformers
function toList(rawData: DocumentItem[]): ListWidgetItem[] {
  return rawData.map(document => ({
    id: document.id,
    name: document.name,
    tags: formatTags(document.tags),
    type: 'Document',
    created_at: document.created_at,
    data: { sections: document.data || [], sectionsCount: document.data?.length || 0 },
    updated_at: document.updated_at,
    description: formatSectionCount(document.data)
  }));
}

function toTable(rawData: DocumentItem[]): TableDataOutput {
  return {
    columns,
    data: rawData.map(document => ({
      id: document.id,
      name: String(document.name),
      tags: formatTags(document.tags),
      sections: String(document.data?.length || 0),
      created_at: formatDate(document.created_at),
      updated_at: formatDate(document.updated_at),
      created_by: String(document.created_by || 'Unknown'),
    })),
    metadata: {
      totalCount: rawData.length,
      filteredCount: rawData.length
    }
  };
}

/**
 * Transform raw data to CardData format
 * @param rawData - Array of DocumentItem from store
 * @param context - Optional context with tags, getUserNameById for resolving IDs
 */
function toCard(rawData: DocumentItem[], context?: TransformerContext): CardData[] {
  const { tags = [], getUserNameById } = context || {};
  
  return rawData.map(doc => {
    const statusColor = getStatusColor(doc.status, 'documents', doc.game_id);
    
    // Resolve creator name
    const creatorName = doc.created_by && getUserNameById 
      ? getUserNameById(doc.created_by) 
      : (doc.created_by || 'Unknown');
    
    // Resolve tag names
    const tagNames = tags.length > 0 
      ? convertTagIdsToNames(doc.tags, tags) 
      : (Array.isArray(doc.tags) ? doc.tags : []);

    return {
      id: doc.id,
      gameId: doc.game_id,
      title: doc.name,
      creator: creatorName,
      owner: creatorName,
      ownerId: doc.created_by || undefined,
      editPermissions: doc.edit_permissions || 'Open',
      tags: tagNames,
      description: (doc.settings?.subtitle as string) || "No description",
      lastUpdate: formatRelativeTime(doc.updated_at),
      version: 1.0,
      icon: doc.settings?.icon as string,
      iconBg: doc.settings?.color as string,
      shadowType: doc.status ? "status" as const : "black" as const,
      shadowColor: statusColor,
      statusColor: statusColor,
      hasVersionMismatch: false,
      status: doc.status,
      folder: doc.folder,
      itemType: 'documents',
    };
  });
}

/**
 * Transform raw data to TableItem[] format for MainTableWidget
 * @param rawData - Array of DocumentItem from store
 * @param context - Optional context with currentUserId, tags lookup
 */
function toTableItems(rawData: DocumentItem[], context?: TransformerContext): TableItemsOutput {
  const { tags = [], getUserNameById, currentUserId } = context || {};
  
  const items: TableDataRow[] = rawData.map(doc => {
    // Determine lock status
    const ownerId = doc.created_by;
    const editPermissions = doc.edit_permissions || 'Open';
    const isLocked = editPermissions === 'Locked' && 
                     ownerId !== undefined && 
                     currentUserId !== undefined && 
                     ownerId !== currentUserId;
    
    // Resolve creator/owner name
    const ownerName = doc.created_by && getUserNameById 
      ? getUserNameById(doc.created_by) 
      : (doc.created_by || 'Unknown');
    
    // Resolve tag names  
    const tagNames = tags.length > 0 
      ? convertTagIdsToNames(doc.tags, tags) 
      : (Array.isArray(doc.tags) ? doc.tags : []);
    
    return {
      type: "row" as const,
      key: doc.id,
      depth: 0,
      isLocked,
      ownerId,
      editPermissions: editPermissions as 'Open' | 'Locked',
      content: TABLE_COLUMNS.map((columnKey, index) => {
        const normalizedKey = columnKey.toLowerCase().replace(/\s+/g, "");
        let content: string | boolean | number = "";

        switch (normalizedKey) {
          case "title":
            content = doc.name || "";
            break;
          case "description":
            content = (doc.settings?.subtitle as string) || "";
            break;
          case "owner":
            content = ownerName;
            break;
          case "tags":
            content = tagNames.join(", ");
            break;
          case "status":
            content = doc.status || "";
            break;
          case "lastupdate":
            content = formatRelativeTime(doc.updated_at);
            break;
          default:
            content = "";
        }

        return {
          key: columnKey,
          fixedX: index === 0,
          content,
        };
      }),
    };
  });

  return {
    items,
    columns: TABLE_COLUMNS,
    fieldMetadata: fields,
  };
}

// Export the module
export const documentsDataSource: DataSourceModule = {
  type: 'documents',
  config,
  fields,
  columns,
  fetchData,
  transformers: {
    toList,
    toTable,
    toCard,
    toTableItems
  }
};

// Export table columns for use in components
export { TABLE_COLUMNS as DOCUMENTS_TABLE_COLUMNS };

